/* eslint-disable no-restricted-syntax */
import {Button, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack} from '@mui/material';
import useDelete from 'hooks/useDelete';
import {useInputMenuContext} from 'context/InputMenu';
import * as I from 'interfaces';
import GenericDialog from 'components/Generic/GenericDialog';
import {useTranslation} from 'react-i18next';
import DialogTitleWithClose from '../Generic/DialogTitle';
import {type Dispatch, type SetStateAction} from 'react';

export default function DeleteAlert({
  open,
  setOpen,
  id,
  type,
  handleCommunicationSucces,
  message,
  title,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  id: string | null;
  type: I.FormSelectionEnum;
  handleCommunicationSucces: () => void;
  message?: JSX.Element | string;
  title?: string;
}) {
  const {t} = useTranslation('common');

  const inputContext = useInputMenuContext();
  const inputMenuDispatch = inputContext?.dispatch;

  const {_delete, setDelete} = useDelete({
    id,
    type,
    method: I.methodType.delete,
    inputMenuDispatch,
    handleCommunicationSucces: () => {
      handleCommunicationSucces();
      setOpen(false);
    },
  });

  const defaultMessage = type === I.FormSelectionEnum.fallowing ? t('removeFallowingConsequences') : t('removeAction');
  return (
    <GenericDialog
      open={open}
      keepMounted
      onClose={() => {
        setOpen(false);
      }}
    >
      <DialogTitle>{title ?? `${t('areYouSureDelete')} ${t(type).toLowerCase()}?`}</DialogTitle>
      <DialogContent>{message ?? <DialogContentText>{defaultMessage}</DialogContentText>}</DialogContent>
      <DialogActions>
        <Button
          disabled={_delete}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(false);
            inputMenuDispatch && inputMenuDispatch({type: I.inputMenuActionType.closePopOver});
          }}
          color="primary"
          data-testid="delete-alert-disagree-button"
        >
          {t('cancel')}
        </Button>
        <Button
          loading={_delete}
          onClick={() => {
            setDelete(true);
            inputMenuDispatch && inputMenuDispatch({type: I.inputMenuActionType.closePopOver});
          }}
          color="primary"
          variant="contained"
          data-testid="delete-alert-agree-button"
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </GenericDialog>
  );
}

export function GenericDeleteAlert<T>({
  open,
  setOpen,
  message,
  title,
  callback,
  isLoading,
  testid,
  callbackData,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  message?: JSX.Element | string;
  title: string;
  callback: (data?: T) => Promise<void>;
  isLoading?: boolean;
  testid?: string;
  callbackData?: T;
}) {
  const {t} = useTranslation('common');

  return (
    <GenericDialog
      open={open}
      keepMounted
      onClose={() => {
        setOpen(false);
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitleWithClose
        onClose={() => {
          setOpen(false);
        }}
      >
        {title || `${t('areYouSureDelete')} ?`}
      </DialogTitleWithClose>
      <DialogContent>
        <Stack direction="row" spacing={2}>
          {message ?? (
            <Stack direction="column">
              {title && <DialogContentText>{`${t('areYouSureDelete')}?`}</DialogContentText>}
              <DialogContentText>{t('removeAction')}</DialogContentText>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isLoading}
          onClick={() => {
            setOpen(false);
          }}
          variant="outlined"
          color="primary"
          data-testid={`delete-alert-disagree-button-${testid}`}
        >
          {t('cancel')}
        </Button>
        <Button
          loading={isLoading}
          onClick={() => {
            void callback(callbackData).then(() => {
              setOpen(false);
            });
          }}
          variant="contained"
          color="primary"
          data-testid={`delete-alert-agree-button-${testid}`}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </GenericDialog>
  );
}
